import MComponent from '../../Toolkit/Components/Component';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import WithRouter from '../../Toolkit/WithRouter';
import React, {createRef, ReactElement, RefObject} from 'react';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import {MonthView, MonthViewMultiReply} from '../../Api/ControllerTypes/JobAssignmentTypes';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {JobAssignmentClient} from '../../Api/JobAssignmentClient';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MButton from '../../Toolkit/Components/Controls/Button';
import CalendarDialog from '../../Dialogs/CalendarDialog';
import styles from './CalendarView.module.css';
import EnumContext from '../../Context/enum-context';
import MFormControl from '../../Toolkit/Components/Controls/FormControl';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {CrewData} from '../../Api/ControllerTypes/CrewTypes';
import {MNullable} from '../../Toolkit/Utils';

type Props = WithMsalProps & WithRouterProps & {};

type FilterData = {
    project: number;
    exchange: string;
    permitStatus: number;
    tmRequired: number;
    crew: number;
}

type State = {
    page: MPageProps;
    displayDate: Date;
    allData: MonthView[];
    itemData: MonthView[];
    projects: MDropDownItem<number>[] | undefined;
    exchanges: MDropDownItem<string>[] | undefined;
    permitStatuses: MDropDownItem<number>[] | undefined;
    tmTypes: MDropDownItem<number>[] | undefined;
    crews: MDropDownItem<number>[] | undefined;
    filterData: FilterData;
};

class CalendarViewPage extends MComponent<Props, State>{
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dateSelectRef: RefObject<CalendarDialog>;
    private readonly m_projectRef: RefObject<MDropDown<number>>;
    private readonly m_exchangeRef: RefObject<MDropDown<string>>;
    private readonly m_permitRef: RefObject<MDropDown<number>>;
    private readonly m_tmRef: RefObject<MDropDown<number>>;
    private readonly m_crewRef: RefObject<MDropDown<number>>;
    private readonly m_jobAssignClient: NSwagAdapter<JobAssignmentClient>;

    public constructor(props: Props){
        super(props);

        this.bindMethods();

        this.m_pageRef = createRef<MPage>();
        this.m_dateSelectRef = createRef<CalendarDialog>();
        this.m_projectRef = createRef<MDropDown<number>>();
        this.m_exchangeRef = createRef<MDropDown<string>>();
        this.m_permitRef = createRef<MDropDown<number>>();
        this.m_tmRef = createRef<MDropDown<number>>();
        this.m_crewRef = createRef<MDropDown<number>>();
        this.m_jobAssignClient = new NSwagAdapter<JobAssignmentClient>(new JobAssignmentClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));

        this.initializeState();
    }

    private bindMethods(): void{
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.nextDay_Click = this.nextDay_Click.bind(this);
        this.previousDay_Click = this.previousDay_Click.bind(this);
        this.selectDay_Click = this.selectDay_Click.bind(this);
        this.onDateSelection = this.onDateSelection.bind(this);
        this.today_Click = this.today_Click.bind(this);
        this.project_OnSelect = this.project_OnSelect.bind(this);
        this.exchange_OnSelect = this.exchange_OnSelect.bind(this);
        this.permit_OnSelect = this.permit_OnSelect.bind(this);
        this.tm_OnSelect = this.tm_OnSelect.bind(this);
        this.crew_OnSelect = this.crew_OnSelect.bind(this);
        this.clearFilter_Click = this.clearFilter_Click.bind(this);
    }

    private initializeState(): void {
        const page: MPageProps = {
            locked: false
        };

        const todayDate = new Date();
        todayDate.setHours(12,0,0);

        const filterData: FilterData = {
            project: -1,
            exchange: '',
            permitStatus: -1,
            tmRequired: -1,
            crew: -1
        }

        this.state = {
            page: page,
            displayDate: todayDate,
            allData: [],
            itemData: [],
            projects: undefined,
            exchanges: undefined,
            permitStatuses: undefined,
            tmTypes: undefined,
            crews: undefined,
            filterData: filterData
        }
    }

    private async nextDay_Click() : Promise<void> {
        const newDay = this.state.displayDate!.getMonth() + 1;
        const newDate = this.state.displayDate!;
        newDate.setMonth(newDay);
        this.safeSetState(draft => draft.displayDate = newDate);
        await this.page_onLoadData(newDate);
    }

    private async previousDay_Click() : Promise<void> {
        const newDay = this.state.displayDate!.getMonth() - 1;
        const newDate = this.state.displayDate!;
        newDate.setMonth(newDay);
        this.safeSetState(draft => draft.displayDate = newDate);
        await this.page_onLoadData(newDate);
    }

    private selectDay_Click(): void {
        this.m_dateSelectRef.current?.show(this.state.displayDate);
    }

    private async onDateSelection(selectedDate: Date): Promise<void> {
        selectedDate.setHours(12,0,0);
        this.safeSetState(draft => draft.displayDate = selectedDate);
        await this.page_onLoadData(selectedDate);
    }

    private async today_Click(): Promise<void> {
        const todayDate = new Date();
        todayDate.setHours(12,0,0);
        this.safeSetState(draft => draft.displayDate = todayDate);
        await this.page_onLoadData(todayDate);
    }
    
    private project_OnSelect(newValue: MNullable<number>): void {
        this.safeSetState(draft => {
            draft.filterData.project = newValue ?? -1;
            draft.itemData = this.filterData(draft.filterData);
        });
    }

    private exchange_OnSelect(newValue: MNullable<string>): void {
        this.safeSetState(draft => {
            draft.filterData.exchange = newValue ?? '';
            draft.itemData = this.filterData(draft.filterData);
        });
    }

    private permit_OnSelect(newValue: MNullable<number>): void {
        this.safeSetState(draft => {
            draft.filterData.permitStatus = newValue ?? -1;
            draft.itemData = this.filterData(draft.filterData);
        });
    }

    private tm_OnSelect(newValue: MNullable<number>): void {
        this.safeSetState(draft => {
            draft.filterData.tmRequired = newValue ?? -1;
            draft.itemData = this.filterData(draft.filterData);
        });
    }

    private crew_OnSelect(newValue: MNullable<number>): void {
        this.safeSetState(draft => {
            draft.filterData.crew = newValue ?? -1;
            draft.itemData = this.filterData(draft.filterData);
        });
    }

    private filterData(filterData: FilterData) : MonthView[] {
        let filteredData = this.state.allData;
        if(filterData.project !== -1) {
            filteredData = filteredData.filter(i => i.request?.projectValue === filterData.project);
        }
        if(filterData.exchange !== '') {
            filteredData = filteredData.filter(i => i.request?.exchange === filterData.exchange);
        }
        if(filterData.permitStatus !== -1) {
            filteredData = filteredData.filter(i => i.request?.permitStatus === filterData.permitStatus);
        }
        if(filterData.tmRequired !== -1) {
            filteredData = filteredData.filter(i => i.jobAssignment?.tmType.includes(filterData.tmRequired));
        }
        if(filterData.crew !== -1) {
            filteredData = filteredData.filter(i => ((i.crewData?.filter(c => c.crew.id === filterData.crew)?.length ?? 0) > 0));
        }

        return filteredData;
    }
    
    private clearFilter_Click(): void {
        this.m_projectRef.current!.setValue(-1);
        this.m_exchangeRef.current!.setValue(' ');
        this.m_permitRef.current!.setValue(-1);
        this.m_tmRef.current!.setValue(-1);
        this.m_crewRef.current!.setValue(-1);
        const filterData: FilterData = {
            project: -1,
            exchange: '',
            permitStatus: -1,
            tmRequired: -1,
            crew: -1
        }
        this.safeSetState(draft => {
            draft.filterData = filterData;
            draft.itemData = draft.allData;
        });
    }

    private onNavigate_Click(requestId: string): void {
        this.props.navigate(`/bookings/${requestId}`);
    }

    private async page_onLoadData(viewDate: Date | undefined = undefined): Promise<void> {
        const queryDate = viewDate === undefined ? this.state.displayDate : viewDate;

        const monthViewResult = await this.m_jobAssignClient.callWithReply(
            MonthViewMultiReply, (client => client.getMonthView(queryDate.getMonth() + 1, queryDate.getFullYear())));
        monthViewResult.throwIfFailed();

        let projects = monthViewResult.reply!.jobAssignments.map(ja => ja.request?.projectValue);
        projects = projects.filter((value, index, array) => array.indexOf(value) === index);
        let exchanges = monthViewResult.reply!.jobAssignments.map(ja => ja.request?.exchange);
        exchanges = exchanges.filter((value, index, array) => array.indexOf(value) === index);
        let permitStatuses = monthViewResult.reply!.jobAssignments.map(ja => ja.request?.permitStatus);
        permitStatuses = permitStatuses.filter((value, index, array) => array.indexOf(value) === index);
        let tmTypes: number[] = [];
        monthViewResult.reply!.jobAssignments.forEach(ja => ja.jobAssignment?.tmType.forEach(jt => tmTypes.push(jt)));
        tmTypes = tmTypes.filter((value, index, array) => array.indexOf(value) === index);
        let crews: CrewData[] = [];
        monthViewResult.reply!.jobAssignments.forEach(ja => ja.crewData?.forEach(c => crews.push(c)));
        crews = crews.filter((value, index, array) => array.findIndex(c => c.crew.id === value.crew.id) === index);

        //Save data to state.
        this.safeSetState((draft) => {
            draft.allData = monthViewResult.reply!.jobAssignments;
            draft.itemData = monthViewResult.reply!.jobAssignments;
            draft.projects = projects.map(p => {
                const index = this.context?.enums?.projectList?.findIndex(proj => proj.id === p)
                return { label: (this.context?.enums?.projectList[index ?? -1].code ?? '') + ' - ' + (this.context?.enums?.projectList[index ?? -1].name ?? ''), value: p! };
            });
            draft.exchanges = exchanges.map(e => { return { label: e ?? '', value: e ?? ''}});
            draft.permitStatuses = permitStatuses.map(p => {
                const index = this.context?.enums?.permitStatuses?.findIndex(perm => perm.id === p)
                return { label: (this.context?.enums?.permitStatuses[index ?? -1]?.name ?? ''), value: p! };
            });
            draft.tmTypes = tmTypes.map(t => {
                const index = this.context?.enums?.jobTypes?.findIndex(jobType => jobType.id === t)
                return { label: (this.context?.enums?.jobTypes[index ?? -1]?.name ?? ''), value: t! };
            });
            draft.crews = crews.map(c => { return { label: c.crew.crewName, value: c.crew.id} });
        })
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MBackButton disabled={false}/>
                <MSpacer/>
                <MButton label="Previous Month"
                         icon={"pi pi-caret-left"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.previousDay_Click}/>
                <MSpacer/>
                <MButton label="Next Month"
                         icon="pi pi-caret-right"
                         iconOnly={false}
                         disabled={false}
                         onClick={this.nextDay_Click}/>
                <MSpacer/>
                <MButton label="Select Date"
                         icon="pi pi-calendar"
                         iconOnly={false}
                         disabled={false}
                         onClick={this.selectDay_Click}/>
                <MSpacer/>
                <MButton label="Today"
                         color="info"
                         icon="pi pi-calendar-times"
                         iconOnly={false}
                         disabled={false}
                         onClick={this.today_Click}/>
            </MToolbar>
        );
    }

    private DayTemplate(dayDate: Date): ReactElement {
        const dayAssignments = this.state.itemData.filter(d => d.jobAssignment.assignedDate!.toDateString() === dayDate.toDateString())

        return <div>
            <div className={styles.date}>
                <p>
                    {dayDate.getDate()}
                </p>
            </div>
            {dayAssignments.map(d => {
                const index = this.context.enums?.jobTypes.findIndex(value => value.id === d.jobAssignment.tmType[0]);
                const tmType = index !== undefined ? this.context.enums?.jobTypes[index]?.name : '';
                return <div className={styles.dataText}
                            style={(d.crewData?.length ?? 0) > 0 ? {background: "#" + d.crewData![0].crew.highlightColour} : {background: "#d32f2f"}}
                     onClick={this.onNavigate_Click.bind(this, d.request!.id)}>{`${d.request?.estimate} (${tmType})`}</div>;
            })}
            </div>;
    }

    public render(): ReactElement {

        const month = this.state.displayDate.getMonth();
        const year = this.state.displayDate.getFullYear();
        const dayOne = new Date(year, month, 1).getDay();
        const weekList: Date[][] = [];

        //Create array of all visible dates.
        for(let weekLoop = 0; weekLoop < 6; weekLoop++) {
            const dayList: Date[] = [];
            for(let dayLoop = 0; dayLoop < 7; dayLoop++) {
                dayList.push(new Date(year, month, (weekLoop * 7) + dayLoop + 1 - dayOne));
            }
            weekList.push(dayList);
        }

        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
        const titleText = `Calendar View - ${monthNames[this.state.displayDate!.getMonth()]}, ${this.state.displayDate!.getFullYear()}`;

        return (
            <MPage title={titleText}
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}            >

                <CalendarDialog onDateSelected={this.onDateSelection}
                                ref={this.m_dateSelectRef}
                />

                <div className={styles.pageLeft}>
                    <div className={styles.parent}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                {dayNames.map((r, index) =>
                                    <th key={r} className={styles.header}>{r}</th>
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {weekList.map((w) => {
                                return (<tr className={styles.dataRow} key={w[0].toDateString()} >
                                    {w.map(r =>
                                        <td key={r.toDateString()} className={styles.data}>{this.DayTemplate(r)}</td>
                                    )}
                                </tr>);
                            })
                            }
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.pageRight}>
                    <div className="formgrid grid">
                        <div className="col-12 md:col-12">
                            <MFormControl label={'Project'} id={'projectFilter'}>
                                {(fieldClasses: string | undefined) => {
                                    return (
                                        <MDropDown id={'projectValue'}
                                                   ref={this.m_projectRef}
                                                   disabled={false}
                                                   items={this.state.projects ?? []}
                                                   initialValue={0}
                                                   onChange={this.project_OnSelect}
                                        />
                                    );
                                }}
                            </MFormControl>
                        </div>

                        <div className="col-12 md:col-12">
                            <MFormControl label={'Exchange'} id={'exchangeFilter'}>
                                {(fieldClasses: string | undefined) => {
                                    return (
                                        <MDropDown id={'exchangeValue'}
                                                   ref={this.m_exchangeRef}
                                                   disabled={false}
                                                   items={this.state.exchanges ?? []}
                                                   initialValue={' '}
                                                   onChange={this.exchange_OnSelect}
                                        />
                                    );
                                }}
                            </MFormControl>
                        </div>

                        <div className="col-12 md:col-12">
                            <MFormControl label={'Permit Status'} id={'permitStatusFilter'}>
                                {(fieldClasses: string | undefined) => {
                                    return (
                                        <MDropDown id={'permitStatusValue'}
                                                   ref={this.m_permitRef}
                                                   disabled={false}
                                                   items={this.state.permitStatuses ?? []}
                                                   initialValue={-1}
                                                   onChange={this.permit_OnSelect}
                                        />
                                    );
                                }}
                            </MFormControl>
                        </div>

                        <div className="col-12 md:col-12">
                            <MFormControl label={'TM Required'} id={'tmRequiredFilter'}>
                                {(fieldClasses: string | undefined) => {
                                    return (
                                        <MDropDown id={'tmRequiredValue'}
                                                   ref={this.m_tmRef}
                                                   disabled={false}
                                                   items={this.state.tmTypes ?? []}
                                                   initialValue={-1}
                                                   onChange={this.tm_OnSelect}
                                        />
                                    );
                                }}
                            </MFormControl>
                        </div>

                        <div className="col-12 md:col-12">
                            <MFormControl label={'Crew'} id={'crewFilter'}>
                                {(fieldClasses: string | undefined) => {
                                    return (
                                        <MDropDown id={'crewValue'}
                                                   ref={this.m_crewRef}
                                                   disabled={false}
                                                   items={this.state.crews ?? []}
                                                   onChange={this.crew_OnSelect}
                                        />
                                    );
                                }}
                            </MFormControl>
                        </div>

                        <div className="col-12 md:col-12">
                            <MButton label="Clear Filter"
                                     disabled={false}
                                     onClick={this.clearFilter_Click}
                            />
                        </div>
                    </div>
                </div>

            </MPage>
        )
    }
}

export default withMsal(WithRouter(CalendarViewPage));